import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Check if the error is likely caused by Cloudflare Access session expiry.
 * When the session expires, asset requests return the CF Access login HTML
 * instead of JS, causing React to crash on load.
 */
function isLikelySessionExpiry(): boolean {
  // If the auth /me check fails with a redirect, session is expired
  // Also detect if we're very early in the app lifecycle (no content rendered)
  const root = document.getElementById('root');
  const hasRenderedContent = root && root.children.length > 0;
  return !hasRenderedContent;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // If the app crashed before rendering anything, it's likely a session expiry
      // where CF Access intercepted asset loads and returned HTML instead of JS
      if (isLikelySessionExpiry()) {
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
          }}>
            <div style={{
              background: 'white', borderRadius: '12px', padding: '32px',
              maxWidth: '360px', textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <svg style={{ margin: '0 auto 16px', display: 'block' }} width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#6b7280">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
              <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                Session Expired
              </h2>
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                Your session has expired. Sign in again to continue.
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#4f46e5', color: 'white', border: 'none',
                  padding: '10px 24px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: 500, cursor: 'pointer', width: '100%',
                }}
              >
                Sign In
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-500 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            {this.state.error?.stack && (
              <details className="text-left mb-4">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  Show error details
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-x-auto max-h-48 overflow-y-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            <button
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              onClick={() => {
                this.setState({ hasError: false, error: undefined });
                window.location.reload();
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
